import { CompanyTitledPage } from "../../TitledPage";

import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid"
import Button from "@mui/material/Button";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from "@mui/material/Typography";

import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { CircleDoubleIcon } from "../../../components/Icons";

import { blue, grey, orange, red, yellow } from "@mui/material/colors";
import { useNavigate, useOutletContext } from "react-router-dom";

import { SYSTEM_PATH_COMPANY } from "../../../App";
import CalendarYearInput from "../../../components/CalendarYearInput";
import { PRINT_MODE_EXCLUDE_HEADER, PRINT_MODE_INCLUDE_HEADER } from "../../SiteHeader";

import { postMarketCalendarSearchInfoListAPI } from "../../../http/api/MarketCalendarAPI";

const headerCellSx={
  backgroundColor: grey[50],
  textAlign: "center",
  padding: 1,
  fontSize: 12,
  borderTop: `1px ${grey[300]} solid`,
}

const cellBorderSx={
  borderRight: `1px ${grey[300]} solid`,
  "&:first-of-type": {
    borderLeft: `1px ${grey[300]} solid`,
  }
}

const MarketClosedIcon = CircleOutlinedIcon;
const MarketTemporaryClosedIcon = CircleDoubleIcon;
const MarketTemporaryOpenedIcon = CircleIcon;

const DAY_ID_SUNDAY = 1;
const DAY_ID_SATURDAY = 7;

function CalendarCell({dateData: dateData}) {

  const marketIconSx = {
    width: 12,
    height: 12,
  };

  const getCalendarCellSx = (dateData) => {
    let sx = {
      ...cellBorderSx,
      verticalAlign: "top",
      textAlign: "center",
      width: 23,
      height: 38,    
      padding: 0.5,
    }
  
    if (dateData && dateData.isMarketClosed) {
      sx["backgroundColor"] = yellow[300];
    } 

    return sx;
  }

  const getDateSx = (dateData) => {

    let sx = {
      textAlign: "center",
      fontSize: 12,
    };

    if (!dateData) {
      return sx
    }
    
    if (dateData.dayId === DAY_ID_SUNDAY || dateData.holidayId) {
      sx["color"] = red[500];
    } else if (dateData.dayId === DAY_ID_SATURDAY) {
      sx["color"] = blue[500];
    }
  
    return sx;
  }
  
  const CalendarCellSignature = ({dateData: dateData}) => {
      
    if (!dateData) {
      return null;
    }
  
    if (dateData.isMarketClosed) {
      return (
        dateData.baseMarketClosingConditionId
          ? <MarketClosedIcon sx={marketIconSx}/>
          : <MarketTemporaryClosedIcon sx={marketIconSx}/>
      );
    }
  
    return (
      dateData.baseMarketClosingConditionId
        ? <MarketTemporaryOpenedIcon sx={marketIconSx} />
        : null
    );
  }  

  return (
    <TableCell sx={getCalendarCellSx(dateData)}>
      <Typography sx={getDateSx(dateData)}>{dateData ? dateData.date : ""}</Typography>
      <CalendarCellSignature dateData={dateData} />
    </TableCell>
  );

}

function MonthCalendar({monthData: monthData}) {
  
  const calendarMonthHeaderSx = {
    backgroundColor: orange[300],
    padding: 1,
    marginBottom: 1,
  }

  const monthSx = {
    fontSize: 18,
  }

  const dayCountSx = {
    fontSize: 12,
  }

  return (
    <Box>
      <Box sx={calendarMonthHeaderSx}>
        <Grid container spacing={1} alignItems="baseline">
          <Grid item xs="auto" sx={monthSx}>
            {monthData.month}月
          </Grid>
          <Grid item xs="auto" sx={dayCountSx}>
            営業日数: {monthData.marketOpenDayCount}
          </Grid>
          <Grid item xs="auto" sx={dayCountSx}>
            休場日数: {monthData.marketCloseDayCount}
          </Grid>
        </Grid>
      </Box>
      <TableContainer component={Box}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{...headerCellSx, ...cellBorderSx, color: red[500]}}>
                日
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx}}>
                月
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx}}>
                火
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx}}>
                水
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx}}>
                木
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx}}>
                金
              </TableCell>
              <TableCell sx={{...headerCellSx, ...cellBorderSx, color: blue[500]}}>
                土
              </TableCell>
            </TableRow>
            {
              monthData.weekList.map((weekData, week_i) => (
                <TableRow key={`row_${monthData.month}_${week_i}`}>
                  {
                    weekData.map((dateData, day) => (
                      <CalendarCell key={`cell_${monthData.month}_${week_i}_${day}`} dateData={dateData} /> 
                    ))
                  }                
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function MarketCalendar({year: year}) {

  const [calendarData, setCalendarData] = useState([]);

  const getMonthCalendarData = (month) => {

    let monthCalendarDataList = calendarData.filter((dayInfo) => dayInfo.month === month);
    
      let firstDayId = monthCalendarDataList[0].dayId;
      let lastDate = monthCalendarDataList.length;
    
      let weekList = Array.from(new Array(6)).map((_, week_i) => {
        return Array.from(new Array(7)).map((_, day_i) => {
          let dateIdxInMonth =  week_i * 7 + day_i - firstDayId + 1;
      
          if (dateIdxInMonth >= 0 && dateIdxInMonth < lastDate) {
            return monthCalendarDataList[dateIdxInMonth];
          }
          
          return null;
        });
      });
    
      let marketCloseDayCount = monthCalendarDataList.filter((dateData) => (dateData.isMarketClosed)).length;
    
      return {
        month: month,
        weekList: weekList,
        marketCloseDayCount: marketCloseDayCount,
        marketOpenDayCount: lastDate - marketCloseDayCount
      }
    }
     
  useEffect(() => {
    if (year) {
      (async () => {
        let res = await postMarketCalendarSearchInfoListAPI({
          startDate: year.toString() + "-01-01",
          endDate: year.toString() + "-12-31"
        });

        setCalendarData(res.marketCalendarInfoList);
      })()
    }
  }, [year])

  if (calendarData.length === 0) {
    return null;
  }

  return (
    <Box>
      {
        Array.from(new Array(2)).map((_, i) => (
          <Grid container spacing={2} marginBottom={2}>
            {
              Array.from(new Array(6)).map((_, j) => (
                <Grid item xs="auto"  sx={{"@media print": {transform: "scale(0.7, 0.7)", transformOrigin: "0 0", marginRight: -9, marginBottom: -13}}}>
                  <MonthCalendar monthData={getMonthCalendarData(6 * i + j + 1)} />
                </Grid>
              ))
            }
          </Grid>    
        ))        
      }
    </Box>      
  );

}

// 営業カレンダーページ
export default function MarketCalendarPage() {
   
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [isPrinting, setPrinting] = useState(false);

  const navigate = useNavigate();
  const {printMode, setPrintMode} = useOutletContext();

  const setPrintModeExcludeHeader = () => {
    setPrinting(true);
    setPrintMode(PRINT_MODE_EXCLUDE_HEADER);
  }

  useEffect(() => {
    if (printMode === PRINT_MODE_EXCLUDE_HEADER && isPrinting) {
      window.print();

      setPrinting(false);
      setPrintMode(PRINT_MODE_INCLUDE_HEADER)
    }
  }, [printMode])
  
  const onClickPrintCalendar = (event) => {
    setPrintModeExcludeHeader();
  }

  const onClickBasicClosedList = (event) => {
    navigate(SYSTEM_PATH_COMPANY + "/basic_closed_list");
  }

  const onClickTemporaryScheduleList = (event) => {
    navigate(SYSTEM_PATH_COMPANY + "/temporary_schedule_list");
  }

  return (
    <CompanyTitledPage title="営業カレンダー" printMode={printMode}>
      <Grid container alignItems="center" spacing={1} marginBottom={2}>
        <Grid item xs="auto" marginRight={4} className={printMode === PRINT_MODE_EXCLUDE_HEADER ? "print_exclude" : ""}>
          <CalendarYearInput calendarYear={calendarYear} setCalendarYear={setCalendarYear} /> 
        </Grid>
        <Grid item xs="auto" marginRight={1}>
          <MarketClosedIcon sx={{width:12, height:12}}/> : 休場日
        </Grid>
        <Grid item xs="auto" marginRight={1}>
          <MarketTemporaryClosedIcon sx={{width:12, height:12}}/> : 臨時休場日
        </Grid>
        <Grid item xs="auto">
          <MarketTemporaryOpenedIcon sx={{width:12, height:12}}/> : 臨時開場日
        </Grid>
        <Grid item xs />
        <Grid item xs="auto" className={printMode === PRINT_MODE_EXCLUDE_HEADER ? "print_exclude" : ""}>
          <Button variant="contained" onClick={onClickPrintCalendar}>カレンダー印刷</Button>
        </Grid>
        <Grid item xs="auto" className={printMode === PRINT_MODE_EXCLUDE_HEADER ? "print_exclude" : ""}>
          <Button variant="contained" onClick={onClickBasicClosedList}>基本休場日</Button>
        </Grid>
        <Grid item xs="auto" className={printMode === PRINT_MODE_EXCLUDE_HEADER ? "print_exclude" : ""}>
          <Button variant="contained" onClick={onClickTemporaryScheduleList}>臨時スケジュール</Button>
        </Grid>
      </Grid>    
      <MarketCalendar year={calendarYear}/>
    </CompanyTitledPage>
  );
}